/**
 * Materials Planning & Purchase Orders Components
 * 
 * NON-NEGOTIABLE:
 * - Jobs own Purchase Orders (POs cannot exist without a Job)
 * - No silent automation (all POs require human confirmation)
 * - Locked lineage (every PO line item references Job ID and source)
 * - Explicit substitutions only (no automatic changes)
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Package,
  Truck,
  Plus,
  Send,
  CheckCircle,
  AlertCircle,
  Clock,
  ShoppingCart,
  FileText,
  X,
  Edit2,
  Loader2,
  AlertTriangle,
  ExternalLink,
  Box,
  DollarSign,
  Calendar,
  Building2,
  Mail,
  Phone,
  ChevronDown,
  ChevronUp,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import GlassPanel from './GlassPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status colors for planning and PO statuses
const planningStatusColors = {
  'PENDING': 'draft',
  'ORDERED': 'completed',
  'PARTIALLY_ORDERED': 'active',
  'CANCELLED': 'overdue'
};

const poStatusColors = {
  'DRAFT': 'draft',
  'SENT': 'pending',
  'CONFIRMED': 'active',
  'ACTION_REQUIRED': 'overdue',
  'DELIVERED': 'completed',
  'CANCELLED': 'overdue'
};

const poStatusLabels = {
  'DRAFT': 'Draft',
  'SENT': 'Sent',
  'CONFIRMED': 'Confirmed',
  'ACTION_REQUIRED': 'Action Required',
  'DELIVERED': 'Delivered',
  'CANCELLED': 'Cancelled'
};

// ============ SOURCE BADGE ============
export function MaterialSourceBadge({ source }) {
  const sourceConfig = {
    'QUOTE_LINE_ITEM': { label: 'From Quote', color: 'var(--accent-primary)', bg: 'rgba(99, 102, 241, 0.1)' },
    'MANUAL': { label: 'Manual', color: 'var(--text-secondary)', bg: 'var(--glass-bg)' },
    'PRICE_INTELLIGENCE': { label: 'Price Intel', color: 'var(--status-warning)', bg: 'rgba(245, 158, 11, 0.1)' }
  };

  const config = sourceConfig[source] || sourceConfig['MANUAL'];

  return (
    <span style={{
      fontSize: '0.6875rem',
      color: config.color,
      background: config.bg,
      padding: '2px 6px',
      borderRadius: 'var(--radius-sm)',
      fontWeight: '500'
    }}>
      {config.label}
    </span>
  );
}

// ============ PO STATUS BADGE ============
export function POStatusBadge({ status }) {
  return (
    <span className={`status-badge ${poStatusColors[status] || 'draft'}`}>
      {poStatusLabels[status] || status}
    </span>
  );
}

// ============ MATERIALS PLANNING PANEL ============
export function MaterialsPlanningPanel({ jobId, orgId, onPOCreated }) {
  const { getAuthHeaders } = useAuth();
  const [planning, setPlanning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPOModal, setShowPOModal] = useState(false);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [expanded, setExpanded] = useState(true);

  const fetchPlanning = useCallback(async () => {
    if (!jobId || !orgId) return;
    
    try {
      setLoading(true);
      const res = await fetch(
        `${API_URL}/api/materials/job/${jobId}/planning?org_id=${orgId}`,
        { headers: getAuthHeaders() }
      );
      
      if (!res.ok) throw new Error('Failed to fetch materials planning');
      const data = await res.json();
      setPlanning(data);
    } catch (err) {
      console.error('Error fetching planning:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jobId, orgId, getAuthHeaders]);

  useEffect(() => {
    fetchPlanning();
  }, [fetchPlanning]);

  const handleMaterialAdded = () => {
    setShowAddModal(false);
    fetchPlanning();
  };

  const handlePOCreated = (po) => {
    setShowPOModal(false);
    setSelectedMaterials([]);
    fetchPlanning();
    if (onPOCreated) onPOCreated(po);
  };

  const toggleMaterialSelection = (materialId) => {
    setSelectedMaterials(prev => 
      prev.includes(materialId) 
        ? prev.filter(id => id !== materialId)
        : [...prev, materialId]
    );
  };

  const pendingMaterials = planning?.materials?.filter(m => m.status === 'PENDING') || [];

  if (loading) {
    return (
      <GlassPanel title="Materials Planning" titleIcon={Package}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-xl)' }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
        </div>
      </GlassPanel>
    );
  }

  if (error) {
    return (
      <GlassPanel title="Materials Planning" titleIcon={Package}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', color: 'var(--status-danger)' }}>
          <AlertCircle size={16} />
          {error}
        </div>
      </GlassPanel>
    );
  }

  return (
    <GlassPanel 
      title="Materials Planning" 
      titleIcon={Package}
      headerAction={
        <button
          onClick={() => setExpanded(!expanded)}
          className="btn btn-secondary"
          style={{ padding: '4px 8px' }}
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      }
    >
      {/* Summary Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: 'var(--space-md)', 
        marginBottom: expanded ? 'var(--space-lg)' : 0 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-primary)' }}>
            {planning?.total_materials || 0}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Total</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--status-warning)' }}>
            {planning?.materials_pending || 0}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Pending</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--status-success)' }}>
            {planning?.materials_ordered || 0}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Ordered</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--accent-primary)' }}>
            £{(planning?.total_estimated_cost || 0).toFixed(2)}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Est. Cost</div>
        </div>
      </div>

      {expanded && (
        <>
          {/* Actions */}
          <div style={{ display: 'flex', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
            <button 
              onClick={() => setShowAddModal(true)}
              className="btn btn-secondary"
              data-testid="add-material-btn"
            >
              <Plus size={14} />
              Add Material
            </button>
            {selectedMaterials.length > 0 && (
              <button 
                onClick={() => setShowPOModal(true)}
                className="btn btn-primary"
                data-testid="create-po-btn"
              >
                <ShoppingCart size={14} />
                Create PO ({selectedMaterials.length} items)
              </button>
            )}
          </div>

          {/* Materials List */}
          {planning?.materials?.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                    <th style={{ width: '30px', padding: 'var(--space-sm)' }}></th>
                    <th style={{ textAlign: 'left', padding: 'var(--space-sm)', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Material</th>
                    <th style={{ textAlign: 'center', padding: 'var(--space-sm)', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Source</th>
                    <th style={{ textAlign: 'center', padding: 'var(--space-sm)', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Qty</th>
                    <th style={{ textAlign: 'right', padding: 'var(--space-sm)', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Est. Total</th>
                    <th style={{ textAlign: 'center', padding: 'var(--space-sm)', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {planning.materials.map((material) => (
                    <tr 
                      key={material.id} 
                      style={{ 
                        borderBottom: '1px solid var(--glass-border)',
                        background: selectedMaterials.includes(material.id) ? 'rgba(99, 102, 241, 0.05)' : 'transparent'
                      }}
                      data-testid={`material-row-${material.id}`}
                    >
                      <td style={{ padding: 'var(--space-sm)', textAlign: 'center' }}>
                        {material.status === 'PENDING' && (
                          <input
                            type="checkbox"
                            checked={selectedMaterials.includes(material.id)}
                            onChange={() => toggleMaterialSelection(material.id)}
                            style={{ cursor: 'pointer' }}
                            data-testid={`select-material-${material.id}`}
                          />
                        )}
                      </td>
                      <td style={{ padding: 'var(--space-sm)' }}>
                        <div style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                          {material.description}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                          {material.category}
                          {material.supplier_preference && ` • ${material.supplier_preference}`}
                        </div>
                      </td>
                      <td style={{ padding: 'var(--space-sm)', textAlign: 'center' }}>
                        <MaterialSourceBadge source={material.source} />
                      </td>
                      <td style={{ padding: 'var(--space-sm)', textAlign: 'center', color: 'var(--text-secondary)' }}>
                        {material.quantity} {material.unit}
                      </td>
                      <td style={{ padding: 'var(--space-sm)', textAlign: 'right', color: 'var(--text-primary)', fontWeight: '500' }}>
                        £{(material.estimated_total || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: 'var(--space-sm)', textAlign: 'center' }}>
                        <span className={`status-badge ${planningStatusColors[material.status] || 'draft'}`}>
                          {material.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: 'var(--space-xl)', 
              color: 'var(--text-muted)',
              background: 'var(--glass-bg)',
              borderRadius: 'var(--radius-md)'
            }}>
              <Package size={32} style={{ marginBottom: 'var(--space-sm)', opacity: 0.5 }} />
              <div>No materials planned yet</div>
              <div style={{ fontSize: '0.8125rem', marginTop: 'var(--space-xs)' }}>
                Add materials manually or they will be inherited from the quote
              </div>
            </div>
          )}
        </>
      )}

      {/* Add Material Modal */}
      <AddMaterialModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        jobId={jobId}
        orgId={orgId}
        onSuccess={handleMaterialAdded}
      />

      {/* Create PO Modal */}
      <CreatePOModal
        isOpen={showPOModal}
        onClose={() => setShowPOModal(false)}
        jobId={jobId}
        orgId={orgId}
        materials={pendingMaterials.filter(m => selectedMaterials.includes(m.id))}
        onSuccess={handlePOCreated}
      />
    </GlassPanel>
  );
}

// ============ ADD MATERIAL MODAL ============
export function AddMaterialModal({ isOpen, onClose, jobId, orgId, onSuccess }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    category: '',
    quantity: '',
    unit: 'each',
    estimated_unit_price: '',
    supplier_preference: '',
    lead_time_days: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `${API_URL}/api/materials/job/${jobId}/planning?org_id=${orgId}`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...formData,
            quantity: parseFloat(formData.quantity) || 1,
            estimated_unit_price: formData.estimated_unit_price ? parseFloat(formData.estimated_unit_price) : null,
            lead_time_days: formData.lead_time_days ? parseInt(formData.lead_time_days) : null
          })
        }
      );

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to add material');
      }

      // Reset form
      setFormData({
        description: '',
        category: '',
        quantity: '',
        unit: 'each',
        estimated_unit_price: '',
        supplier_preference: '',
        lead_time_days: '',
        notes: ''
      });

      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--space-xl)'
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        style={{
          background: 'var(--card-bg)',
          borderRadius: 'var(--radius-lg)',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          border: '1px solid var(--glass-border)'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          padding: 'var(--space-lg)',
          borderBottom: '1px solid var(--glass-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', margin: 0, fontSize: '1.125rem' }}>
            <Package size={20} />
            Add Material
          </h2>
          <button 
            onClick={onClose} 
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
            data-testid="close-add-material-modal"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: 'var(--space-lg)' }}>
            {error && (
              <div style={{
                padding: 'var(--space-md)',
                background: 'var(--status-danger-bg)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--space-lg)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)'
              }}>
                <AlertCircle size={16} style={{ color: 'var(--status-danger)' }} />
                <span style={{ color: 'var(--status-danger)' }}>{error}</span>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Description *</label>
              <input
                type="text"
                className="form-input"
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Bathroom Tiles - 600x600mm Porcelain"
                required
                data-testid="material-description-input"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
              <div className="form-group">
                <label className="form-label">Category *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.category}
                  onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="e.g., tiles, plumbing"
                  required
                  data-testid="material-category-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Supplier (optional)</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.supplier_preference}
                  onChange={e => setFormData(prev => ({ ...prev, supplier_preference: e.target.value }))}
                  placeholder="e.g., Tile Giant"
                  data-testid="material-supplier-input"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-md)' }}>
              <div className="form-group">
                <label className="form-label">Quantity *</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.quantity}
                  onChange={e => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                  placeholder="25"
                  min="0.01"
                  step="0.01"
                  required
                  data-testid="material-quantity-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Unit</label>
                <select
                  className="form-input"
                  value={formData.unit}
                  onChange={e => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                  data-testid="material-unit-select"
                >
                  <option value="each">each</option>
                  <option value="sqm">sqm</option>
                  <option value="lm">linear m</option>
                  <option value="kg">kg</option>
                  <option value="bags">bags</option>
                  <option value="boxes">boxes</option>
                  <option value="packs">packs</option>
                  <option value="rolls">rolls</option>
                  <option value="sheets">sheets</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Est. Unit Price</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>£</span>
                  <input
                    type="number"
                    className="form-input"
                    style={{ paddingLeft: '28px' }}
                    value={formData.estimated_unit_price}
                    onChange={e => setFormData(prev => ({ ...prev, estimated_unit_price: e.target.value }))}
                    placeholder="45.00"
                    min="0"
                    step="0.01"
                    data-testid="material-price-input"
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Lead Time (days)</label>
              <input
                type="number"
                className="form-input"
                value={formData.lead_time_days}
                onChange={e => setFormData(prev => ({ ...prev, lead_time_days: e.target.value }))}
                placeholder="7"
                min="0"
                data-testid="material-leadtime-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                className="form-input"
                value={formData.notes}
                onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
                rows={2}
                data-testid="material-notes-input"
              />
            </div>
          </div>

          <div style={{ 
            padding: 'var(--space-lg)', 
            borderTop: '1px solid var(--glass-border)',
            display: 'flex',
            gap: 'var(--space-md)',
            justifyContent: 'flex-end'
          }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
              data-testid="submit-add-material-btn"
            >
              {loading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={16} />}
              Add Material
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ CREATE PO MODAL ============
export function CreatePOModal({ isOpen, onClose, jobId, orgId, materials, onSuccess }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    supplier_name: '',
    supplier_email: '',
    supplier_phone: '',
    expected_delivery_date: '',
    notes: ''
  });
  const [lineItems, setLineItems] = useState([]);

  // Initialize line items from materials
  useEffect(() => {
    if (materials && materials.length > 0) {
      setLineItems(materials.map(m => ({
        planned_material_id: m.id,
        description: m.description,
        category: m.category,
        quantity: m.quantity,
        unit: m.unit,
        unit_price: m.estimated_unit_price || 0
      })));

      // Pre-fill supplier if all materials have same preference
      const suppliers = [...new Set(materials.map(m => m.supplier_preference).filter(Boolean))];
      if (suppliers.length === 1) {
        setFormData(prev => ({ ...prev, supplier_name: suppliers[0] }));
      }
    }
  }, [materials]);

  const updateLineItem = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `${API_URL}/api/materials/job/${jobId}/purchase-orders?org_id=${orgId}`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            supplier_name: formData.supplier_name,
            supplier_email: formData.supplier_email || null,
            supplier_phone: formData.supplier_phone || null,
            expected_delivery_date: formData.expected_delivery_date || null,
            notes: formData.notes || null,
            line_items: lineItems.map(li => ({
              planned_material_id: li.planned_material_id,
              quantity: parseFloat(li.quantity),
              unit_price: parseFloat(li.unit_price)
            }))
          })
        }
      );

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create purchase order');
      }

      const data = await res.json();
      if (onSuccess) onSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const subtotal = calculateSubtotal();
  const vat = subtotal * 0.2;
  const total = subtotal + vat;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--space-xl)'
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        style={{
          background: 'var(--card-bg)',
          borderRadius: 'var(--radius-lg)',
          width: '100%',
          maxWidth: '700px',
          maxHeight: '90vh',
          overflow: 'auto',
          border: '1px solid var(--glass-border)'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          padding: 'var(--space-lg)',
          borderBottom: '1px solid var(--glass-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', margin: 0, fontSize: '1.125rem' }}>
            <ShoppingCart size={20} />
            Create Purchase Order
          </h2>
          <button 
            onClick={onClose} 
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
            data-testid="close-create-po-modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Step Indicator */}
        <div style={{ 
          display: 'flex', 
          padding: 'var(--space-md) var(--space-lg)',
          borderBottom: '1px solid var(--glass-border)'
        }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ 
              width: '24px', 
              height: '24px', 
              borderRadius: '50%', 
              background: step >= 1 ? 'var(--accent-primary)' : 'var(--glass-border)',
              color: step >= 1 ? 'white' : 'var(--text-muted)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: '600'
            }}>1</div>
            <div style={{ fontSize: '0.75rem', color: step >= 1 ? 'var(--text-primary)' : 'var(--text-muted)', marginTop: '4px' }}>
              Review Items
            </div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ 
              width: '24px', 
              height: '24px', 
              borderRadius: '50%', 
              background: step >= 2 ? 'var(--accent-primary)' : 'var(--glass-border)',
              color: step >= 2 ? 'white' : 'var(--text-muted)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: '600'
            }}>2</div>
            <div style={{ fontSize: '0.75rem', color: step >= 2 ? 'var(--text-primary)' : 'var(--text-muted)', marginTop: '4px' }}>
              Supplier Details
            </div>
          </div>
        </div>

        <div style={{ padding: 'var(--space-lg)' }}>
          {error && (
            <div style={{
              padding: 'var(--space-md)',
              background: 'var(--status-danger-bg)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-lg)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)'
            }}>
              <AlertCircle size={16} style={{ color: 'var(--status-danger)' }} />
              <span style={{ color: 'var(--status-danger)' }}>{error}</span>
            </div>
          )}

          {step === 1 && (
            <>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: 'var(--space-lg)' }}>
                Review and adjust quantities and prices before creating the purchase order.
              </p>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                      <th style={{ textAlign: 'left', padding: 'var(--space-sm)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>Item</th>
                      <th style={{ textAlign: 'center', padding: 'var(--space-sm)', fontSize: '0.75rem', color: 'var(--text-muted)', width: '100px' }}>Qty</th>
                      <th style={{ textAlign: 'right', padding: 'var(--space-sm)', fontSize: '0.75rem', color: 'var(--text-muted)', width: '120px' }}>Unit Price</th>
                      <th style={{ textAlign: 'right', padding: 'var(--space-sm)', fontSize: '0.75rem', color: 'var(--text-muted)', width: '100px' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, index) => (
                      <tr key={item.planned_material_id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                        <td style={{ padding: 'var(--space-sm)' }}>
                          <div style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{item.description}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{item.category}</div>
                        </td>
                        <td style={{ padding: 'var(--space-sm)' }}>
                          <input
                            type="number"
                            className="form-input"
                            value={item.quantity}
                            onChange={e => updateLineItem(index, 'quantity', e.target.value)}
                            min="0.01"
                            step="0.01"
                            style={{ textAlign: 'center' }}
                            data-testid={`po-qty-${index}`}
                          />
                        </td>
                        <td style={{ padding: 'var(--space-sm)' }}>
                          <div style={{ position: 'relative' }}>
                            <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>£</span>
                            <input
                              type="number"
                              className="form-input"
                              style={{ paddingLeft: '24px', textAlign: 'right' }}
                              value={item.unit_price}
                              onChange={e => updateLineItem(index, 'unit_price', e.target.value)}
                              min="0"
                              step="0.01"
                              data-testid={`po-price-${index}`}
                            />
                          </div>
                        </td>
                        <td style={{ padding: 'var(--space-sm)', textAlign: 'right', fontWeight: '500', color: 'var(--text-primary)' }}>
                          £{(item.quantity * item.unit_price).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--glass-border)' }}>
                      <td colSpan="3" style={{ padding: 'var(--space-sm)', textAlign: 'right', color: 'var(--text-muted)' }}>Subtotal</td>
                      <td style={{ padding: 'var(--space-sm)', textAlign: 'right', fontWeight: '500' }}>£{subtotal.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td colSpan="3" style={{ padding: 'var(--space-sm)', textAlign: 'right', color: 'var(--text-muted)' }}>VAT (20%)</td>
                      <td style={{ padding: 'var(--space-sm)', textAlign: 'right', fontWeight: '500' }}>£{vat.toFixed(2)}</td>
                    </tr>
                    <tr style={{ background: 'var(--glass-bg)' }}>
                      <td colSpan="3" style={{ padding: 'var(--space-sm)', textAlign: 'right', fontWeight: '600', color: 'var(--text-primary)' }}>Total</td>
                      <td style={{ padding: 'var(--space-sm)', textAlign: 'right', fontWeight: '700', color: 'var(--accent-primary)', fontSize: '1.125rem' }}>£{total.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: 'var(--space-lg)' }}>
                Enter supplier details. The PO will be created as a draft for you to review and send.
              </p>

              <div className="form-group">
                <label className="form-label">Supplier Name *</label>
                <div style={{ position: 'relative' }}>
                  <Building2 size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input
                    type="text"
                    className="form-input"
                    style={{ paddingLeft: '36px' }}
                    value={formData.supplier_name}
                    onChange={e => setFormData(prev => ({ ...prev, supplier_name: e.target.value }))}
                    placeholder="e.g., Tile Giant"
                    required
                    data-testid="po-supplier-name-input"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <div style={{ position: 'relative' }}>
                    <Mail size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                      type="email"
                      className="form-input"
                      style={{ paddingLeft: '36px' }}
                      value={formData.supplier_email}
                      onChange={e => setFormData(prev => ({ ...prev, supplier_email: e.target.value }))}
                      placeholder="orders@supplier.com"
                      data-testid="po-supplier-email-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <div style={{ position: 'relative' }}>
                    <Phone size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                      type="tel"
                      className="form-input"
                      style={{ paddingLeft: '36px' }}
                      value={formData.supplier_phone}
                      onChange={e => setFormData(prev => ({ ...prev, supplier_phone: e.target.value }))}
                      placeholder="0123 456 7890"
                      data-testid="po-supplier-phone-input"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Expected Delivery Date</label>
                <div style={{ position: 'relative' }}>
                  <Calendar size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input
                    type="date"
                    className="form-input"
                    style={{ paddingLeft: '36px' }}
                    value={formData.expected_delivery_date}
                    onChange={e => setFormData(prev => ({ ...prev, expected_delivery_date: e.target.value }))}
                    data-testid="po-delivery-date-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Notes for Supplier</label>
                <textarea
                  className="form-input"
                  value={formData.notes}
                  onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="e.g., Delivery to site address, call on arrival..."
                  rows={3}
                  data-testid="po-notes-input"
                />
              </div>

              {/* Summary */}
              <div style={{ 
                background: 'var(--glass-bg)', 
                padding: 'var(--space-md)', 
                borderRadius: 'var(--radius-md)',
                marginTop: 'var(--space-lg)'
              }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: 'var(--space-sm)' }}>Order Summary</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{lineItems.length} items</span>
                  <span style={{ fontWeight: '700', fontSize: '1.25rem', color: 'var(--accent-primary)' }}>£{total.toFixed(2)}</span>
                </div>
              </div>
            </>
          )}
        </div>

        <div style={{ 
          padding: 'var(--space-lg)', 
          borderTop: '1px solid var(--glass-border)',
          display: 'flex',
          gap: 'var(--space-md)',
          justifyContent: 'flex-end'
        }}>
          {step === 1 ? (
            <>
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => setStep(2)}
                data-testid="po-next-step-btn"
              >
                Next: Supplier Details
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>
                Back
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={loading || !formData.supplier_name}
                data-testid="po-create-btn"
              >
                {loading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <ShoppingCart size={16} />}
                Create PO (Draft)
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ PURCHASE ORDERS LIST ============
export function PurchaseOrdersList({ jobId, orgId }) {
  const { getAuthHeaders } = useAuth();
  const [pos, setPOs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPO, setExpandedPO] = useState(null);

  const fetchPOs = useCallback(async () => {
    if (!jobId || !orgId) return;
    
    try {
      setLoading(true);
      const [posRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/materials/job/${jobId}/purchase-orders?org_id=${orgId}`, {
          headers: getAuthHeaders()
        }),
        fetch(`${API_URL}/api/materials/job/${jobId}/purchase-orders/summary?org_id=${orgId}`, {
          headers: getAuthHeaders()
        })
      ]);

      if (posRes.ok) {
        const data = await posRes.json();
        setPOs(data.purchase_orders || []);
      }

      if (summaryRes.ok) {
        setSummary(await summaryRes.json());
      }
    } catch (err) {
      console.error('Error fetching POs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jobId, orgId, getAuthHeaders]);

  useEffect(() => {
    fetchPOs();
  }, [fetchPOs]);

  const handleStatusChange = async (poId, newStatus) => {
    try {
      const endpoint = {
        'SENT': 'send',
        'CONFIRMED': 'confirm',
        'DELIVERED': 'delivered',
        'CANCELLED': 'cancel'
      }[newStatus];

      const res = await fetch(
        `${API_URL}/api/materials/purchase-orders/${poId}/${endpoint}?org_id=${orgId}`,
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );

      if (!res.ok) throw new Error('Failed to update status');
      fetchPOs();
    } catch (err) {
      console.error('Error updating PO status:', err);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <GlassPanel title="Purchase Orders" titleIcon={Truck}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-xl)' }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
        </div>
      </GlassPanel>
    );
  }

  return (
    <GlassPanel 
      title="Purchase Orders" 
      titleIcon={Truck}
      headerAction={
        <button onClick={fetchPOs} className="btn btn-secondary" style={{ padding: '4px 8px' }}>
          <RefreshCw size={14} />
        </button>
      }
    >
      {/* Summary Stats */}
      {summary && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: 'var(--space-md)', 
          marginBottom: 'var(--space-lg)',
          padding: 'var(--space-md)',
          background: 'var(--glass-bg)',
          borderRadius: 'var(--radius-md)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--text-primary)' }}>
              {summary.total_pos}
            </div>
            <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>Total POs</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--status-warning)' }}>
              {summary.draft_pos + summary.sent_pos}
            </div>
            <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>Pending</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--status-success)' }}>
              {summary.confirmed_pos + summary.delivered_pos}
            </div>
            <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>Confirmed</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--accent-primary)' }}>
              £{(summary.total_committed || 0).toFixed(2)}
            </div>
            <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>Committed</div>
          </div>
        </div>
      )}

      {/* Action Required Alert */}
      {summary?.action_required_pos > 0 && (
        <div style={{
          padding: 'var(--space-md)',
          background: 'var(--status-danger-bg)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-lg)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)'
        }}>
          <AlertTriangle size={16} style={{ color: 'var(--status-danger)' }} />
          <span style={{ color: 'var(--status-danger)', fontWeight: '500' }}>
            {summary.action_required_pos} PO(s) require action - substitution approval needed
          </span>
        </div>
      )}

      {/* POs List */}
      {pos.length > 0 ? (
        <div>
          {pos.map(po => (
            <div 
              key={po.id}
              className="glass-panel"
              style={{ marginBottom: 'var(--space-sm)', padding: 'var(--space-md)' }}
              data-testid={`po-item-${po.id}`}
            >
              <div 
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  cursor: 'pointer'
                }}
                onClick={() => setExpandedPO(expandedPO === po.id ? null : po.id)}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{po.po_number}</span>
                    <POStatusBadge status={po.status} />
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                    {po.supplier_name}
                    {po.created_at && ` • ${new Date(po.created_at).toLocaleDateString('en-GB')}`}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: '600', color: 'var(--accent-primary)' }}>
                    £{(po.total || 0).toFixed(2)}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    inc. VAT
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedPO === po.id && (
                <div style={{ marginTop: 'var(--space-md)', paddingTop: 'var(--space-md)', borderTop: '1px solid var(--glass-border)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
                    {po.supplier_email && (
                      <div>
                        <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>Email</div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{po.supplier_email}</div>
                      </div>
                    )}
                    {po.supplier_phone && (
                      <div>
                        <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>Phone</div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{po.supplier_phone}</div>
                      </div>
                    )}
                    {po.expected_delivery_date && (
                      <div>
                        <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>Expected Delivery</div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                          {new Date(po.expected_delivery_date).toLocaleDateString('en-GB')}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
                    {po.status === 'DRAFT' && (
                      <>
                        <button 
                          className="btn btn-primary"
                          onClick={() => handleStatusChange(po.id, 'SENT')}
                          data-testid={`send-po-${po.id}`}
                        >
                          <Send size={14} />
                          Mark as Sent
                        </button>
                        <button 
                          className="btn btn-secondary"
                          onClick={() => handleStatusChange(po.id, 'CANCELLED')}
                        >
                          <X size={14} />
                          Cancel
                        </button>
                      </>
                    )}
                    {po.status === 'SENT' && (
                      <button 
                        className="btn btn-primary"
                        onClick={() => handleStatusChange(po.id, 'CONFIRMED')}
                        data-testid={`confirm-po-${po.id}`}
                      >
                        <CheckCircle size={14} />
                        Mark Confirmed
                      </button>
                    )}
                    {po.status === 'CONFIRMED' && (
                      <button 
                        className="btn btn-primary"
                        onClick={() => handleStatusChange(po.id, 'DELIVERED')}
                        data-testid={`deliver-po-${po.id}`}
                      >
                        <Truck size={14} />
                        Mark Delivered
                      </button>
                    )}
                    {po.status === 'ACTION_REQUIRED' && (
                      <button className="btn btn-primary" style={{ background: 'var(--status-danger)' }}>
                        <AlertTriangle size={14} />
                        Review Substitution
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          padding: 'var(--space-xl)', 
          color: 'var(--text-muted)',
          background: 'var(--glass-bg)',
          borderRadius: 'var(--radius-md)'
        }}>
          <Truck size={32} style={{ marginBottom: 'var(--space-sm)', opacity: 0.5 }} />
          <div>No purchase orders yet</div>
          <div style={{ fontSize: '0.8125rem', marginTop: 'var(--space-xs)' }}>
            Create a PO from the Materials Planning section
          </div>
        </div>
      )}
    </GlassPanel>
  );
}

// ============ COMMITTED COSTS WIDGET ============
export function CommittedCostsWidget({ jobId, orgId }) {
  const { getAuthHeaders } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      if (!jobId || !orgId) return;
      
      try {
        const res = await fetch(
          `${API_URL}/api/materials/job/${jobId}/purchase-orders/summary?org_id=${orgId}`,
          { headers: getAuthHeaders() }
        );
        
        if (res.ok) {
          setSummary(await res.json());
        }
      } catch (err) {
        console.error('Error fetching PO summary:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [jobId, orgId, getAuthHeaders]);

  if (loading || !summary) return null;

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 'var(--space-md)',
      padding: 'var(--space-md)',
      background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(34, 197, 94, 0.1) 100%)',
      borderRadius: 'var(--radius-md)',
      border: '1px solid rgba(34, 197, 94, 0.2)'
    }}>
      <DollarSign size={20} style={{ color: 'var(--status-success)' }} />
      <div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Committed Costs</div>
        <div style={{ fontWeight: '700', color: 'var(--status-success)' }}>
          £{(summary.total_committed || 0).toFixed(2)}
        </div>
      </div>
      <div style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
        {summary.confirmed_pos + summary.delivered_pos} POs confirmed
      </div>
    </div>
  );
}

export default MaterialsPlanningPanel;
